const NESTED_SPEC_KEYS = ['layer', 'concat', 'hconcat', 'vconcat', 'spec'];

const getMarkType = (mark: unknown): string | undefined => {
  if (typeof mark === 'string') return mark;
  if (typeof mark !== 'object' || mark === null) return undefined;

  const markType = (mark as Record<string, unknown>).type;
  return typeof markType === 'string' ? markType : undefined;
};

// Recursively inject compact number formatting ("~s": SI prefix, e.g. 14M, 50K)
// on any quantitative axis encoding that does not already have an explicit format.
// Only touches `type: "quantitative"` channels so temporal/ordinal axes are unaffected.
export function injectCompactAxisFormat(spec: Record<string, unknown>): void {
  if (spec.encoding && typeof spec.encoding === 'object') {
    const encoding = spec.encoding as Record<string, unknown>;
    for (const channel of ['x', 'y', 'x2', 'y2']) {
      const enc = encoding[channel] as Record<string, unknown> | undefined;
      if (enc && enc.type === 'quantitative') {
        const axis = (enc.axis as Record<string, unknown> | null) ?? {};
        if (!axis.format) {
          enc.axis = { ...axis, format: '~s' };
        }
      }
    }
  }

  for (const key of NESTED_SPEC_KEYS) {
    const child = spec[key];
    if (Array.isArray(child)) {
      for (const sub of child) {
        if (typeof sub === 'object' && sub !== null) {
          injectCompactAxisFormat(sub as Record<string, unknown>);
        }
      }
    } else if (typeof child === 'object' && child !== null) {
      injectCompactAxisFormat(child as Record<string, unknown>);
    }
  }
}

const DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;
const DATE_FORMAT_DIRECTIVE = /%(?:a|A|b|B|d|e|g|G|j|m|q|u|U|V|w|W|x|y|Y)/;
const TIME_FORMAT_DIRECTIVE = /%(?:c|f|H|I|L|M|p|Q|s|S|X)/;
const DATE_ONLY_VALUE = /^\d{4}-\d{2}-\d{2}$/;
const TEMPORAL_TICK_PROPERTIES = ['tickCount', 'tickMinStep', 'values'];

const hasDateOnlyFormat = (format: unknown): format is string =>
  typeof format === 'string' &&
  DATE_FORMAT_DIRECTIVE.test(format) &&
  !TIME_FORMAT_DIRECTIVE.test(format);

const getInlineDataRows = (
  spec: Record<string, unknown>,
  inheritedRows: Record<string, unknown>[] = [],
  datasets: Record<string, unknown> | undefined = undefined,
): Record<string, unknown>[] => {
  const data = spec.data;
  if (data === undefined) return inheritedRows;

  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return [];
  }

  const dataDefinition = data as Record<string, unknown>;
  const values =
    typeof dataDefinition.name === 'string' && datasets !== undefined
      ? datasets[dataDefinition.name]
      : dataDefinition.values;
  if (!Array.isArray(values)) return [];

  if (
    !values.every(
      (value): value is Record<string, unknown> =>
        typeof value === 'object' && value !== null && !Array.isArray(value),
    )
  ) {
    return [];
  }

  return values;
};

const CONFIDENCE_PRESENTATION_CHANNELS = ['color', 'fill', 'stroke'];
const POSITION_CHANNELS = ['x', 'y'];
const MAX_CONFIDENCE_CATEGORICAL_COLORS = 8;
const CONFIDENCE_OVERFLOW_CATEGORICAL_SCHEME = 'tableau20';
const CONFIDENCE_LINE_DASH_PATTERNS = [
  [],
  [8, 4],
  [2, 3],
  [10, 3, 2, 3],
  [1, 4],
  [12, 3, 3, 3],
  [6, 2, 1, 2],
  [5, 5],
];

const hasExplicitStrokeDash = (mark: unknown): boolean =>
  typeof mark === 'object' &&
  mark !== null &&
  !Array.isArray(mark) &&
  'strokeDash' in mark;

const getStrokeLegend = (legend: unknown): Record<string, unknown> | null =>
  legend === null
    ? null
    : {
        symbolType: 'stroke',
        ...(typeof legend === 'object' && !Array.isArray(legend) ? legend : {}),
      };

const getCategoricalPositionField = (
  encoding: Record<string, unknown>,
): string | undefined => {
  for (const channel of POSITION_CHANNELS) {
    const encodedField = encoding[channel];
    if (typeof encodedField !== 'object' || encodedField === null) continue;

    const fieldDefinition = encodedField as Record<string, unknown>;
    if (
      typeof fieldDefinition.field === 'string' &&
      (fieldDefinition.type === 'nominal' || fieldDefinition.type === 'ordinal')
    ) {
      return fieldDefinition.field;
    }
  }

  return undefined;
};

const getDistinctValueCount = (
  rows: Record<string, unknown>[],
  field: string,
): number =>
  new Set(
    rows
      .map(row => row[field])
      .filter(value => value !== null && value !== undefined),
  ).size;

const applyFoldTransforms = (
  rows: Record<string, unknown>[],
  transform: unknown,
): Record<string, unknown>[] => {
  if (!Array.isArray(transform)) return rows;

  return transform.reduce<Record<string, unknown>[]>(
    (transformedRows, step) => {
      if (typeof step !== 'object' || step === null || Array.isArray(step)) {
        return transformedRows;
      }

      const fold = (step as Record<string, unknown>).fold;
      if (
        !Array.isArray(fold) ||
        !fold.every(field => typeof field === 'string')
      ) {
        return transformedRows;
      }

      const as = (step as Record<string, unknown>).as;
      const [keyField, valueField] =
        Array.isArray(as) &&
        as.length === 2 &&
        as.every(field => typeof field === 'string')
          ? as
          : ['key', 'value'];

      return transformedRows.flatMap(row =>
        fold.map(field => ({
          ...row,
          [keyField]: field,
          [valueField]: row[field],
        })),
      );
    },
    rows,
  );
};

const isCategoricalFieldDefinition = (
  encodedField: Record<string, unknown>,
): boolean =>
  typeof encodedField.field === 'string' &&
  (encodedField.type === 'nominal' || encodedField.type === 'ordinal');

const hasExplicitMarkPresentation = (mark: unknown): boolean =>
  typeof mark === 'object' &&
  mark !== null &&
  !Array.isArray(mark) &&
  CONFIDENCE_PRESENTATION_CHANNELS.some(
    channel => (mark as Record<string, unknown>)[channel] !== undefined,
  );

const removeRedundantBarValueColor = (
  localEncoding: Record<string, unknown>,
  encoding: Record<string, unknown>,
): void => {
  const quantitativePositionFields = new Set(
    POSITION_CHANNELS.flatMap(channel => {
      const encodedField = encoding[channel];
      if (typeof encodedField !== 'object' || encodedField === null) return [];

      const fieldDefinition = encodedField as Record<string, unknown>;
      return fieldDefinition.type === 'quantitative' &&
        typeof fieldDefinition.field === 'string'
        ? [fieldDefinition.field]
        : [];
    }),
  );

  for (const channel of CONFIDENCE_PRESENTATION_CHANNELS) {
    const encodedField = encoding[channel];
    if (typeof encodedField !== 'object' || encodedField === null) continue;

    const fieldDefinition = encodedField as Record<string, unknown>;
    if (
      fieldDefinition.type === 'quantitative' &&
      typeof fieldDefinition.field === 'string' &&
      quantitativePositionFields.has(fieldDefinition.field)
    ) {
      Reflect.deleteProperty(localEncoding, channel);
      Reflect.deleteProperty(encoding, channel);
    }
  }
};

const normalizeCategoricalBarValueScale = (
  encoding: Record<string, unknown>,
): void => {
  if (encoding.x2 !== undefined || encoding.y2 !== undefined) return;

  for (const [categoryChannel, valueChannel] of [
    ['x', 'y'],
    ['y', 'x'],
  ]) {
    const categoryEncoding = encoding[categoryChannel];
    const valueEncoding = encoding[valueChannel];
    if (
      typeof categoryEncoding !== 'object' ||
      categoryEncoding === null ||
      typeof valueEncoding !== 'object' ||
      valueEncoding === null
    ) {
      continue;
    }

    const categoryFieldDefinition = categoryEncoding as Record<string, unknown>;
    const valueFieldDefinition = valueEncoding as Record<string, unknown>;
    if (
      !isCategoricalFieldDefinition(categoryFieldDefinition) ||
      valueFieldDefinition.type !== 'quantitative' ||
      valueFieldDefinition.scale === null
    ) {
      continue;
    }

    const scale = valueFieldDefinition.scale;
    valueFieldDefinition.scale = {
      ...(typeof scale === 'object' && !Array.isArray(scale) ? scale : {}),
      zero: true,
    };
    return;
  }
};

const getCategoricalBarScale = (
  distinctValueCount: number,
  distinctColors: readonly string[],
): Record<string, unknown> | undefined => {
  if (distinctValueCount < 5) return undefined;

  return distinctValueCount <= MAX_CONFIDENCE_CATEGORICAL_COLORS
    ? { range: [...distinctColors] }
    : { scheme: CONFIDENCE_OVERFLOW_CATEGORICAL_SCHEME };
};

const injectConfidenceSeriesStylesInSpec = (
  spec: Record<string, unknown>,
  distinctColors: readonly string[],
  inheritedEncoding: Record<string, unknown> = {},
  inheritedRows: Record<string, unknown>[] = [],
  inheritedDatasets: Record<string, unknown> | undefined = undefined,
): void => {
  const datasets =
    typeof spec.datasets === 'object' &&
    spec.datasets !== null &&
    !Array.isArray(spec.datasets)
      ? (spec.datasets as Record<string, unknown>)
      : inheritedDatasets;
  const rows = applyFoldTransforms(
    getInlineDataRows(spec, inheritedRows, datasets),
    spec.transform,
  );
  const localEncoding =
    typeof spec.encoding === 'object' && spec.encoding !== null
      ? (spec.encoding as Record<string, unknown>)
      : {};
  const encoding = { ...inheritedEncoding, ...localEncoding };
  const markType = getMarkType(spec.mark);

  if (markType === 'line' || markType === 'bar') {
    if (markType === 'bar') {
      removeRedundantBarValueColor(localEncoding, encoding);
      normalizeCategoricalBarValueScale(encoding);
    }

    for (const channel of CONFIDENCE_PRESENTATION_CHANNELS) {
      const encodedField = encoding[channel];
      if (typeof encodedField !== 'object' || encodedField === null) continue;

      const categoricalEncoding = encodedField as Record<string, unknown>;
      if (!isCategoricalFieldDefinition(categoricalEncoding)) continue;
      const field = categoricalEncoding.field;
      if (typeof field !== 'string') continue;

      const distinctValueCount = getDistinctValueCount(rows, field);
      if (
        distinctValueCount < 5 ||
        (markType === 'line' &&
          distinctValueCount > MAX_CONFIDENCE_CATEGORICAL_COLORS)
      ) {
        continue;
      }

      const scale = categoricalEncoding.scale;
      if (scale === null) continue;
      if (
        typeof scale === 'object' &&
        !Array.isArray(scale) &&
        ('range' in scale || 'scheme' in scale)
      ) {
        continue;
      }

      if (markType === 'bar') {
        categoricalEncoding.scale = {
          ...(typeof scale === 'object' && !Array.isArray(scale) ? scale : {}),
          ...getCategoricalBarScale(distinctValueCount, distinctColors),
        };
      } else {
        if (
          encoding.strokeDash === undefined &&
          !hasExplicitStrokeDash(spec.mark)
        ) {
          localEncoding.strokeDash = {
            field,
            type: categoricalEncoding.type,
            scale: {
              range: CONFIDENCE_LINE_DASH_PATTERNS.map(pattern => [...pattern]),
            },
          };
        }

        const strokeDashEncoding =
          localEncoding.strokeDash ?? encoding.strokeDash;
        if (
          typeof strokeDashEncoding === 'object' &&
          strokeDashEncoding !== null &&
          !Array.isArray(strokeDashEncoding) &&
          (strokeDashEncoding as Record<string, unknown>).field === field
        ) {
          const lineLegend = getStrokeLegend(categoricalEncoding.legend);
          categoricalEncoding.legend = lineLegend;
          (strokeDashEncoding as Record<string, unknown>).legend = lineLegend;
        }
      }
      break;
    }

    if (
      markType === 'bar' &&
      !hasExplicitMarkPresentation(spec.mark) &&
      !CONFIDENCE_PRESENTATION_CHANNELS.some(
        channel => encoding[channel] !== undefined,
      )
    ) {
      const categoryField = getCategoricalPositionField(encoding);
      if (categoryField !== undefined) {
        const distinctValueCount = getDistinctValueCount(rows, categoryField);
        if (distinctValueCount >= 2) {
          const categoricalBarScale = getCategoricalBarScale(
            distinctValueCount,
            distinctColors,
          );
          localEncoding.color = {
            field: categoryField,
            type: 'nominal',
            ...(categoricalBarScale !== undefined
              ? { scale: categoricalBarScale }
              : {}),
            legend: null,
          };
        }
      }
    }
  }

  for (const key of NESTED_SPEC_KEYS) {
    const child = spec[key];
    if (Array.isArray(child)) {
      for (const childSpec of child) {
        if (typeof childSpec === 'object' && childSpec !== null) {
          injectConfidenceSeriesStylesInSpec(
            childSpec as Record<string, unknown>,
            distinctColors,
            encoding,
            rows,
            datasets,
          );
        }
      }
    } else if (typeof child === 'object' && child !== null) {
      injectConfidenceSeriesStylesInSpec(
        child as Record<string, unknown>,
        distinctColors,
        encoding,
        rows,
        datasets,
      );
    }
  }
};

export const injectConfidenceSeriesStyles = (
  spec: Record<string, unknown>,
  distinctColors: readonly string[],
): void => injectConfidenceSeriesStylesInSpec(spec, distinctColors);

const hasDateOnlyValues = (
  field: unknown,
  rows: Record<string, unknown>[],
): boolean =>
  typeof field === 'string' &&
  rows.length > 0 &&
  rows.every(row =>
    typeof row[field] === 'string'
      ? DATE_ONLY_VALUE.test(row[field] as string)
      : false,
  );

const injectUtcScale = (temporalEncoding: Record<string, unknown>): void => {
  const scale = temporalEncoding.scale;
  if (scale === undefined) {
    temporalEncoding.scale = { type: 'utc' };
  } else if (
    typeof scale === 'object' &&
    scale !== null &&
    !Array.isArray(scale) &&
    !('type' in scale)
  ) {
    temporalEncoding.scale = {
      ...(scale as Record<string, unknown>),
      type: 'utc',
    };
  }
};

const normalizeDateOnlyTemporalAxesInSpec = (
  spec: Record<string, unknown>,
  inheritedRows: Record<string, unknown>[] = [],
): void => {
  const rows = getInlineDataRows(spec, inheritedRows);
  if (spec.encoding && typeof spec.encoding === 'object') {
    const encoding = spec.encoding as Record<string, unknown>;
    for (const channel of ['x', 'y']) {
      const encodedField = encoding[channel];
      if (typeof encodedField !== 'object' || encodedField === null) continue;

      const temporalEncoding = encodedField as Record<string, unknown>;
      const axis = temporalEncoding.axis;
      if (
        temporalEncoding.type !== 'temporal' ||
        typeof axis !== 'object' ||
        axis === null
      ) {
        continue;
      }

      const temporalAxis = axis as Record<string, unknown>;
      if (!hasDateOnlyFormat(temporalAxis.format)) continue;

      if (hasDateOnlyValues(temporalEncoding.field, rows)) {
        injectUtcScale(temporalEncoding);
      }

      const hasExplicitTickConfiguration = TEMPORAL_TICK_PROPERTIES.some(
        property => property in temporalAxis,
      );
      if (!hasExplicitTickConfiguration) {
        temporalAxis.tickMinStep = DAY_IN_MILLISECONDS;
      }
    }
  }

  for (const key of NESTED_SPEC_KEYS) {
    const child = spec[key];
    if (Array.isArray(child)) {
      for (const subSpec of child) {
        if (typeof subSpec === 'object' && subSpec !== null) {
          normalizeDateOnlyTemporalAxesInSpec(
            subSpec as Record<string, unknown>,
            rows,
          );
        }
      }
    } else if (typeof child === 'object' && child !== null) {
      normalizeDateOnlyTemporalAxesInSpec(
        child as Record<string, unknown>,
        rows,
      );
    }
  }
};

export function normalizeDateOnlyTemporalAxes(
  spec: Record<string, unknown>,
): void {
  normalizeDateOnlyTemporalAxesInSpec(spec);
}

const pathMarkTypes = ['line', 'area'];

const enableMarkTooltip = (spec: Record<string, unknown>): void => {
  const markValue = spec.mark;
  let mark: Record<string, unknown> | undefined;
  if (typeof markValue === 'string') {
    mark = { type: markValue };
  } else if (typeof markValue === 'object' && markValue !== null) {
    mark = markValue as Record<string, unknown>;
  }

  if (!mark) return;

  if (mark.tooltip === false) {
    spec.mark = mark;
    return;
  }

  const markType = String(mark.type);
  if (!pathMarkTypes.includes(markType)) {
    mark.tooltip ??= true;
    spec.mark = mark;
    return;
  }

  mark.tooltip = false;
  mark.point = { opacity: 0, size: 400, tooltip: true };
  spec.mark = mark;
};

const removeVisiblePointLayers = (spec: Record<string, unknown>): void => {
  if (!Array.isArray(spec.layer)) return;

  const hasPathLayer = spec.layer.some(
    layer =>
      typeof layer === 'object' &&
      layer !== null &&
      pathMarkTypes.includes(
        getMarkType((layer as Record<string, unknown>).mark) ?? '',
      ),
  );
  if (!hasPathLayer) return;

  spec.layer = spec.layer.filter(layer => {
    if (typeof layer !== 'object' || layer === null) return true;
    const markType = getMarkType((layer as Record<string, unknown>).mark);
    return markType !== 'point' && markType !== 'circle';
  });
};

const injectTooltipInteractivityInSpec = (
  spec: Record<string, unknown>,
): void => {
  removeVisiblePointLayers(spec);
  enableMarkTooltip(spec);

  for (const key of NESTED_SPEC_KEYS) {
    const child = spec[key];
    if (Array.isArray(child)) {
      for (const childSpec of child) {
        if (typeof childSpec === 'object' && childSpec !== null) {
          injectTooltipInteractivityInSpec(
            childSpec as Record<string, unknown>,
          );
        }
      }
    } else if (typeof child === 'object' && child !== null) {
      injectTooltipInteractivityInSpec(child as Record<string, unknown>);
    }
  }
};

export const injectTooltipInteractivity = (
  spec: Record<string, unknown>,
): void => {
  const isVegaLite =
    typeof spec.$schema === 'string' && spec.$schema.includes('vega-lite');
  if (!isVegaLite) return;

  injectTooltipInteractivityInSpec(spec);
};
